/**
 * SentenceHighlighter Component
 * Renders text split into sentences with optional highlighting
 * Used for inline TTS highlighting in titles, headers, and other text content
 */

import React from 'react'
import { splitIntoSentences } from '../../lib/utils/tts'
import type { TTSSection } from './TextToSpeechPlayer'
import { TTS_CONFIG } from '../../lib/entities'

interface SentenceHighlighterProps {
    /** The text content to split and render */
    text: string
    /** Currently highlighted section type (e.g., 'title', 'header', 'body') */
    highlightedSection: TTSSection | null
    /** Index of the currently highlighted sentence within the section */
    highlightedIndex: number | null
    /** The section this text belongs to */
    section: TTSSection
    /** Optional style overrides for the highlight */
    highlightStyle?: React.CSSProperties
}

/**
 * Renders text split into sentences with conditional highlighting
 * Each sentence is wrapped in a span with highlighting applied when it matches
 * the current highlighted section and index
 *
 * @example
 * <SentenceHighlighter
 *   text="Hello world. Good morning."
 *   highlightedSection="title"
 *   highlightedIndex={0}
 *   section="title"
 * />
 * // Renders: <span style={{highlight}}>Hello world.</span> <span>Good morning.</span>
 */
export const SentenceHighlighter: React.FC<SentenceHighlighterProps> =
    React.memo(
        ({
            text,
            highlightedSection,
            highlightedIndex,
            section,
            highlightStyle = TTS_CONFIG.HIGHLIGHT,
        }) => {
            const sentences = splitIntoSentences(text)

            return (
                <>
                    {sentences.map((sentence, idx) => {
                        const isHighlighted =
                            highlightedSection === section &&
                            highlightedIndex === idx

                        return (
                            <span
                                key={idx}
                                style={isHighlighted ? highlightStyle : undefined}
                            >
                                {sentence + ' '}
                            </span>
                        )
                    })}
                </>
            )
        }
    )

SentenceHighlighter.displayName = 'SentenceHighlighter'
